import {
  Box,
  IconButton,
  InputAdornment,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
  Tooltip,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { useLocation, useNavigate } from 'react-router-dom';
import { useFilters } from '../TrackerFilter/Context/FiltersContext';
import { useState } from 'react';
import { RouteInformation } from '../../shared/constants/RouteInformation';
import {
  contentLogCategories,
  engagementLogCategories,
  trackerHeaderAccentSoftByType,
  mediaInteractionCategories,
  trackerHeaderBlueByType,
  trackerHeaderBlueHoverByType,
  servicePageCategories,
  TrackerDataType,
} from '../../shared/constants/constants';
import isFilterEmpty from '../../shared/functions/isFilterEmpty';
import ClearIcon from '@mui/icons-material/Clear';
import {
  trackerToolbarButtonTypographySx,
  trackerToolbarNeutralOutlineColor,
} from '../TrackerFilter/trackerFilterToolbarStyles';

export default function TrackerTableFuzzySearch() {
  const { state, pathname } = useLocation();
  const navigate = useNavigate();
  const existingFilters = useFilters();
  const [globalQuery, setGlobalQuery] = useState(state?.queryString ? state.queryString : '');
  const [textFieldValue, setTextFieldValue] = useState(state?.queryString ? state.queryString : '');

  let categories;
  switch (pathname) {
    case RouteInformation.mediaInteraction.path:
      categories = mediaInteractionCategories;
      break;
    case RouteInformation.engagementLog.path:
      categories = engagementLogCategories;
      break;
    case RouteInformation.contentLog.path:
      categories = contentLogCategories;
      break;
    default:
      categories = servicePageCategories;
      break;
  }

  const [selectedCategory, setSelectedCategory] = useState(state?.category || categories[0]);

  const handleClick = () => {
    navigate(pathname, {
      state: {
        queryString: globalQuery,
        category: selectedCategory,
      },
    });
  };

  const updateGlobalQuery = (e: any) => {
    setTextFieldValue(e.target.value);
    setGlobalQuery(e.target.value);
  };

  const handleEnterKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' && globalQuery) {
      handleClick();
    }
  };

  const handleCategoryChange = (e: SelectChangeEvent) => {
    setTextFieldValue('');
    setSelectedCategory(e.target.value);
  };

  const handleTextFieldClear = () => {
    setTextFieldValue('');
    setGlobalQuery('');
    navigate(pathname);
  };

  const getSearchPlaceholder = () => {
    switch (pathname) {
      case RouteInformation.engagementLog.path:
        return 'Search engagements...';
      case RouteInformation.mediaInteraction.path:
        return 'Search media interactions...';
      case RouteInformation.contentLog.path:
        return 'Search content...';
      default:
        return 'Search service topics...';
    }
  };

  const getTrackerType = () => {
    switch (pathname) {
      case RouteInformation.mediaInteraction.path:
        return TrackerDataType.MEDIA_INTERACTION;
      case RouteInformation.engagementLog.path:
        return TrackerDataType.ENGAGEMENT;
      case RouteInformation.contentLog.path:
        return TrackerDataType.CONTENT_LOG;
      default:
        return TrackerDataType.SERVICE;
    }
  };

  const trackerType = getTrackerType();
  const headerBlue = trackerHeaderBlueByType[trackerType];
  const headerBlueHover = trackerHeaderBlueHoverByType[trackerType];
  const trackerAccentSoft = trackerHeaderAccentSoftByType[trackerType];
  const searchOutlineColor = trackerToolbarNeutralOutlineColor;

  return (
    <Box
      sx={{
        display: 'flex',
        border: `1px solid ${searchOutlineColor}`,
        borderRadius: 1,
        height: '32px',
        minWidth: 0,
        flex: '0 1 620px',
        backgroundColor: '#FFFFFF',
        '&:focus-within': {
          boxShadow: `0 0 0 1px ${trackerAccentSoft}`,
        },
      }}
    >
      <Tooltip title={'Change search criteria'} placement={'top-end'} enterDelay={1000}>
        <Select
          sx={{
            height: '100%',
            border: 'none',
            '& fieldset': { border: 'none' },
            borderRight: `1px solid ${searchOutlineColor}`,
            borderRadius: 0,
            '& .MuiSelect-select': {
              display: 'flex',
              alignItems: 'center',
              height: '100%',
              minHeight: 'unset',
              py: 0,
              pl: 1.5,
              pr: 3.75,
            },
            '& .MuiSelect-icon': {
              right: 8,
            },
            ...trackerToolbarButtonTypographySx,
          }}
          MenuProps={{
            anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
            transformOrigin: { vertical: 'top', horizontal: 'left' },
            PaperProps: {
              sx: {
                mt: '2px',
              },
            },
          }}
          disabled={!isFilterEmpty(existingFilters)}
          labelId="search-category"
          id="search-category-select"
          value={selectedCategory}
          label="Category"
          onChange={handleCategoryChange}
          data-cy="search-category-select"
        >
          {categories.map((category) => (
            <MenuItem
              key={category}
              value={category}
              data-cy={`category-${category}`}
              sx={{ ...trackerToolbarButtonTypographySx }}
            >
              {category}
            </MenuItem>
          ))}
        </Select>
      </Tooltip>
      <TextField
        disabled={!isFilterEmpty(existingFilters)}
        placeholder={getSearchPlaceholder()}
        sx={{
          '& .MuiInputBase-input': {
            fontFamily: '"Open Sans", Arial, sans-serif',
          },
          '& input::placeholder': {
            fontSize: '14px',
            fontFamily: '"Open Sans", Arial, sans-serif',
          },
        }}
        onChange={updateGlobalQuery}
        onKeyDown={(e) => handleEnterKeyDown(e)}
        value={textFieldValue}
        autoComplete="off"
        InputProps={{
          sx: {
            height: '100%',
            '& fieldset': { border: 'none' },
            '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
            p: 0,
            width: '100%',
            minWidth: '220px',
            fontFamily: '"Open Sans", Arial, sans-serif',
          },
          endAdornment: (
            <InputAdornment position="end">
              {state?.queryString && (
                <IconButton
                  size="small"
                  onClick={() => handleTextFieldClear()}
                  data-cy="FuzzySearch-TextField-clear-btn"
                >
                  <ClearIcon fontSize="inherit" />
                </IconButton>
              )}
              <IconButton
                sx={{
                  height: '32px',
                  borderLeft: `1px solid ${searchOutlineColor}`,
                  borderTop: 'none',
                  borderBottom: 'none',
                  borderRight: 'none',
                  borderTopLeftRadius: 0,
                  borderBottomLeftRadius: 0,
                  borderTopRightRadius: 1,
                  borderBottomRightRadius: 1,
                  backgroundColor: headerBlue,
                  color: '#FFFFFF',
                  '&:hover': { backgroundColor: headerBlueHover },
                }}
                disabled={!globalQuery}
                onClick={() => handleClick()}
                data-cy="FuzzySearch-TextField-search-btn"
              >
                <SearchIcon />
              </IconButton>
            </InputAdornment>
          ),
        }}
        fullWidth
        data-cy="FuzzySearch-TextField"
      />
    </Box>
  );
}
